/**
 * A pausa da fila do stage, no cartão do pedido que a causou (adendo I2,
 * spec 2026-10-15 §5.4). Quem aprova no eixo devops do stage vê "Retomar a
 * fila" depois de sanear; o resto vê o motivo e quem pode retomar. A rota
 * decide de novo no clique (eixo e sessão de celular).
 */
import { useState } from 'react'
import { Badge, Button, Group, Stack, Text } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { retomarFilaDoStage, type PausaDoStage } from '@/api/terraformRunner'
import { Dado } from './Dado'

const BARLOW = '"Barlow", Arial, sans-serif'
/** As queries que mostram o pedido: o cartão do chat e o painel do chamado. */
const CHAVES_DO_PEDIDO = new Set(['terraform-change-request', 'terraform-pedido-do-chamado'])

function motivoDaRecusa(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  const motivo = (detail as { motivo?: unknown } | undefined)?.motivo
  return typeof motivo === 'string' && motivo ? motivo : 'Não foi possível retomar a fila.'
}

export function PausaDaFila({ pausa }: { pausa: PausaDoStage }) {
  const qc = useQueryClient()
  const [retomada, setRetomada] = useState(false)
  const retomar = useMutation({
    mutationFn: () => retomarFilaDoStage(pausa.stage_key),
    onSuccess: () => setRetomada(true),
    // Qualquer desfecho relê o pedido — no chat e no chamado: a pausa some dele.
    onSettled: () => qc.invalidateQueries({
      predicate: (q) => CHAVES_DO_PEDIDO.has(String(q.queryKey[0])) }),
  })
  return (
    <Stack gap={4}>
      <Group gap={6} wrap="nowrap" align="flex-start">
        <Badge size="xs" color="yellow" variant="light" style={{ flexShrink: 0 }}>
          Fila do stage pausada
        </Badge>
        <Text size="sm" style={{ fontFamily: BARLOW }}>
          {pausa.motivo}. Nenhum pedido da fila de <Dado>{pausa.stage_key}</Dado> roda até o stage
          ser saneado
          {pausa.task_request_id != null && <> (chamado <Dado>{`#${pausa.task_request_id}`}</Dado>)</>}.
        </Text>
      </Group>
      {retomada ? (
        <Text size="sm" style={{ fontFamily: BARLOW }}>
          Fila retomada. Se o stage ainda estiver sujo, o plan do próximo pedido mostra a deriva e a fila pausa de novo.
        </Text>
      ) : pausa.pode_retomar ? (
        <Group gap="xs">
          <Button size="xs" color="inovareTeal" variant="filled"
                  loading={retomar.isPending} onClick={() => retomar.mutate()}>
            Retomar a fila
          </Button>
        </Group>
      ) : (
        <Text size="xs" c="dimmed" style={{ fontFamily: BARLOW }}>
          Quem aprova no eixo devops deste stage retoma a fila depois de sanear.
        </Text>
      )}
      {retomar.isError && (
        <Text size="sm" c="red" style={{ fontFamily: BARLOW }}>
          {motivoDaRecusa(retomar.error)}
        </Text>
      )}
    </Stack>
  )
}
